import {Box, Stack, Tooltip, Typography} from "@mui/material";
import type {IntelReference} from "../../../../../sdo/IntelReference.ts";

interface IntelReferenceCardProps {
    reference: IntelReference;
    selected?: boolean;
    onClick?: (reference: IntelReference) => void;
}

function metadataCount(metadata: Record<string, string> | undefined | null): number {
    if (!metadata) return 0;
    return Object.keys(metadata).length;
}

function IntelReferenceCard({reference, selected = false, onClick}: IntelReferenceCardProps) {
    const clickable = !!onClick;
    const displayName = reference.display && reference.display.trim().length > 0
        ? reference.display
        : reference.definitionKey;
    const frameCount = reference.frames ? reference.frames.length : 0;
    const metaCount = metadataCount(reference.metadata);

    const handleClick = () => {
        if (onClick) onClick(reference);
    };

    return (
        <Box
            onClick={clickable ? handleClick : undefined}
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "300px",
                background: "linear-gradient(155deg, rgba(0, 71, 106, 0.45) 0%, rgba(0, 38, 53, 0.45) 25%, rgba(0, 0, 0, 0.45) 90%, rgba(0, 0, 0, 0.55) 100%)",
                borderRadius: "10px",
                border: selected ? "1px solid #38ff82" : "0.5px solid #3F464F",
                overflow: "hidden",
                cursor: clickable ? "pointer" : "default",
                transition: "border-color 0.2s",
                "&:hover": clickable ? {borderColor: selected ? "#38ff82" : "rgba(56, 255, 130, 0.45)"} : {},
            }}
        >
            {/* Top: icon + display name */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{padding: "8px 10px 4px"}}>
                <img
                    src={`/images/intel/${reference.typeKey}.svg`}
                    width={20}
                    height={20}
                    alt=""
                    aria-hidden="true"
                    style={{opacity: 0.9, flexShrink: 0}}
                    onError={(e) => { (e.target as HTMLImageElement).src = "/images/intel/user_profile.svg"; }}
                />
                <Tooltip title={displayName} arrow placement="top">
                    <Typography sx={{
                        color: "#f2f5fa",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                    }}>
                        {displayName}
                    </Typography>
                </Tooltip>
            </Stack>

            {/* Type / definition */}
            <Typography sx={{
                color: "#02cfee",
                fontSize: "0.72rem",
                fontWeight: 700,
                textAlign: "left",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                padding: "0 10px",
            }}>
                {reference.typeKey} / {reference.definitionKey}
            </Typography>

            {/* Counters footer */}
            <Stack direction="row" spacing={1.5} sx={{padding: "4px 10px 8px"}}>
                <Typography sx={{
                    color: "rgba(242, 245, 250, 0.55)",
                    fontSize: "0.72rem",
                    textAlign: "left",
                }}>
                    Frames:&nbsp;<span style={{color: "#38ff82", fontWeight: 700}}>{frameCount}</span>
                </Typography>
                <Typography sx={{
                    color: "rgba(242, 245, 250, 0.55)",
                    fontSize: "0.72rem",
                    textAlign: "left",
                }}>
                    Metadata:&nbsp;<span style={{color: "#38ff82", fontWeight: 700}}>{metaCount}</span>
                </Typography>
            </Stack>
        </Box>
    );
}

export default IntelReferenceCard;
